import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Typography,
  Chip,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LockIcon from '@mui/icons-material/Lock';
import CheckInTab from './components/CheckInTab';
import CheckOutTab from './components/CheckOutTab';
import OverviewTab from './components/OverviewTab';
import KioskSetup from './components/KioskSetup';
import PinDialog from './components/PinDialog';
import { hasKioskToken, getStats } from './visitorKioskApi';

// Fontana brand color
const FONTANA_BLUE = '#1a365d';

type TabValue = 'checkin' | 'checkout' | 'overview';

interface SuccessMessage {
  type: 'checkin' | 'checkout';
  message: string;
}

export default function BesokshanteringPage() {
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);
  const [currentTab, setCurrentTab] = useState<TabValue>('checkin');
  const [successMessage, setSuccessMessage] = useState<SuccessMessage | null>(null);
  const [visitorCount, setVisitorCount] = useState<number>(0);
  
  // PIN authentication state for overview tab
  const [overviewUnlocked, setOverviewUnlocked] = useState(false);
  const [showPinDialog, setShowPinDialog] = useState(false);

  // Check if kiosk token is configured on mount
  useEffect(() => {
    setIsConfigured(hasKioskToken());
  }, []);

  // Poll for visitor count
  const fetchVisitorCount = useCallback(async () => {
    if (!hasKioskToken()) return;
    try {
      const stats = await getStats();
      setVisitorCount(stats.inBuildingCount);
    } catch {
      // Silently ignore errors for the count badge
    }
  }, []);

  useEffect(() => {
    if (isConfigured) {
      fetchVisitorCount();
      const intervalId = setInterval(fetchVisitorCount, 15000);
      return () => clearInterval(intervalId);
    }
  }, [isConfigured, fetchVisitorCount]);

  const handleSetupComplete = () => {
    setIsConfigured(true);
  };

  const handleCheckInSuccess = () => {
    setSuccessMessage({
      type: 'checkin',
      message: 'Tack! Du är nu incheckad. Välkommen!',
    });
    fetchVisitorCount();
    setTimeout(() => {
      setCurrentTab('checkin');
    }, 3000);
  };

  const handleCheckOutSuccess = () => {
    setSuccessMessage({
      type: 'checkout',
      message: 'Du är nu utcheckad. Ha en bra dag!',
    });
    fetchVisitorCount();
    setTimeout(() => {
      setCurrentTab('checkin');
    }, 3000);
  };

  const handleCloseSuccess = () => {
    setSuccessMessage(null);
  };

  const handleCountChange = (count: number) => {
    setVisitorCount(count);
  };

  // Handle tab changes - intercept overview tab if not unlocked
  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    if (newValue === 'overview') {
      // Always require PIN for overview tab
      setShowPinDialog(true);
    } else {
      // Lock overview when switching away
      setOverviewUnlocked(false);
      setCurrentTab(newValue);
    }
  };

  const handlePinSuccess = () => {
    setOverviewUnlocked(true);
    setShowPinDialog(false);
    setCurrentTab('overview');
  };

  const handlePinClose = () => {
    setShowPinDialog(false);
  };

  // Show loading while checking config
  if (isConfigured === null) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: FONTANA_BLUE,
        }}
      >
        <Typography variant="h6" sx={{ color: 'white' }}>
          Laddar...
        </Typography>
      </Box>
    );
  }

  // Show setup screen if not configured
  if (!isConfigured) {
    return <KioskSetup onSetupComplete={handleSetupComplete} />;
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#F5F7FA',
        overflow: 'hidden', // Prevent scrolling on iPad
      }}
    >
      {/* Blue Header Bar */}
      <Box
        sx={{
          bgcolor: FONTANA_BLUE,
          color: 'white',
          py: 2,
          px: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 72,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography 
              variant="h5" 
              component="h1" 
              sx={{ 
                fontWeight: 700, 
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.3rem', sm: '1.5rem' },
              }}
            >
              Besökshantering
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                opacity: 0.85,
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
              }}
            >
              Incheckning för byggnaden
            </Typography>
          </Box>
        </Box>
        
        {/* Visitor count badge */}
        <Chip
          icon={
            <FiberManualRecordIcon 
              sx={{ 
                fontSize: 14, 
                color: visitorCount > 0 ? '#4CAF50' : 'rgba(255,255,255,0.5)',
              }} 
            />
          }
          label={`${visitorCount} besökare i byggnaden`}
          sx={{
            bgcolor: 'rgba(255,255,255,0.15)',
            color: 'white',
            borderRadius: 4,
            px: 1.5,
            py: 2.5,
            height: 'auto',
            '& .MuiChip-label': {
              fontSize: '1rem',
              fontWeight: 500,
              px: 1,
            },
            '& .MuiChip-icon': {
              ml: 1,
            },
          }}
        />
      </Box>

      {/* Tabs Bar */}
      <Box
        sx={{
          bgcolor: 'white',
          display: 'flex',
          justifyContent: 'center',
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{
            bgcolor: '#F0F2F5',
            borderRadius: 3,
            p: 0.5,
            minHeight: 52,
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTab-root': {
              minHeight: 48,
              minWidth: 140,
              borderRadius: 2.5,
              px: 4,
              mx: 0.5,
              fontSize: '1rem',
              fontWeight: 500,
              textTransform: 'none',
              color: 'text.secondary',
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                bgcolor: 'white',
                color: 'text.primary',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              },
            },
          }}
        >
          <Tab
            value="checkin"
            label="Checka in"
            icon={<PersonAddIcon sx={{ fontSize: 22 }} />}
            iconPosition="start"
          />
          <Tab
            value="checkout"
            label="Checka ut"
            icon={<ExitToAppIcon sx={{ fontSize: 22 }} />}
            iconPosition="start"
          />
          <Tab
            value="overview"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Översikt
                <LockIcon sx={{ fontSize: 16, opacity: 0.6 }} />
              </Box>
            }
            icon={<DashboardIcon sx={{ fontSize: 22 }} />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Main Content Area - Scrollable */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          py: 3,
          px: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 680,
          }}
        >
          {currentTab === 'checkin' && (
            <CheckInTab onSuccess={handleCheckInSuccess} />
          )}
          {currentTab === 'checkout' && (
            <CheckOutTab onSuccess={handleCheckOutSuccess} />
          )}
          {currentTab === 'overview' && (
            <OverviewTab onCountChange={handleCountChange} />
          )}
        </Box>
      </Box>

      {/* Blue Footer Bar with Fontana Logo */}
      <Box
        sx={{
          bgcolor: FONTANA_BLUE,
          py: 1.5,
          px: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 56,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src="/Fontana_Logo_Neg.png"
          alt="Fontana"
          sx={{
            height: 32,
            width: 'auto',
            opacity: 0.95,
          }}
        />
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: 100 }}
      >
        <Alert
          onClose={handleCloseSuccess}
          severity="success"
          variant="filled"
          sx={{
            width: '100%',
            fontSize: '1.3rem',
            py: 2.5,
            px: 5,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              fontSize: '2.5rem',
            },
          }}
        >
          {successMessage?.message}
        </Alert>
      </Snackbar>

      {/* PIN Dialog for Overview access */}
      <PinDialog
        open={showPinDialog}
        onClose={handlePinClose}
        onSuccess={handlePinSuccess}
      />
    </Box>
  );
}
